import { useEffect, useState, useCallback, useMemo } from 'react'
import {Users, FileText, Mail, Calendar, Check, X, Eye, Filter, Download, Briefcase, Phone, MapPin, Clock, Info, CheckCircle2, XCircle, AlarmClock, } from 'lucide-react'
import EmpresaLayout from '@/components/EmpresaLayout'
import {
  useApplications, type Application,
  APPLICATION_STATUS_LABELS, APPLICATION_STATUS_STYLES,
  INTERVIEW_STATUS_LABELS, INTERVIEW_STATUS_STYLES,
} from '@/hooks/useApplications'
import { useAuth } from '@/hooks/useAuth'
import { useNotifications } from '@/hooks/useNotifications'
import { lumi } from '@/lib/lumi'

const statusOptions: Application['status'][] = ['pendiente', 'revisado', 'entrevista', 'aceptado', 'rechazado']

interface CollaboratorProfile {
  fullName?: string
  photoUrl?: string
  email?: string
  phone?: string
  address?: string
  city?: string
  state?: string
  education?: string
  skills?: string[]
  certifications?: string[]
  languages?: string[]
  experience?: string
  cvUrl?: string
  salaryExpectation?: number
  professionalLinks?: string
}

/* ──────────────── helpers ──────────────── */
function fmtDate(iso?: string) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })
}
function fmtDateTime(iso?: string) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

/* ──────────────── componente principal ──────────────── */
function EmpresaPostulantes() {
  const { user } = useAuth()
  const { applications, loading, fetchByCompany, updateStatus, updateInterview } = useApplications()
  const { notifyUser } = useNotifications()

  const [filterStatus, setFilterStatus] = useState<string>('todos')
  const [filterVacancy, setFilterVacancy] = useState<string>('todas')

  // Modales
  const [profileModal, setProfileModal] = useState<Application | null>(null)
  const [interviewModal, setInterviewModal] = useState<Application | null>(null)
  const [colabProfile, setColabProfile] = useState<CollaboratorProfile | null>(null)
  const [loadingProfile, setLoadingProfile] = useState(false)
  const [working, setWorking] = useState(false)

  // Form entrevista
  const [iDate, setIDate] = useState('')
  const [iAddress, setIAddress] = useState('')
  const [iPhone, setIPhone] = useState('')
  const [iNotes, setINotes] = useState('')

  const reload = useCallback(() => {
    if (user) fetchByCompany(user.userId)
  }, [user, fetchByCompany])

  useEffect(() => { reload() }, [reload])

  const vacancyNames = useMemo(() => {
    const map = new Map<string, string>()
    applications.forEach((a) => map.set(a.vacancyId, a.vacancyTitle || 'Vacante'))
    return Array.from(map.entries())
  }, [applications])

  const filtered = useMemo(() => {
    return applications.filter((a) => {
      if (filterStatus !== 'todos' && a.status !== filterStatus) return false
      if (filterVacancy !== 'todas' && a.vacancyId !== filterVacancy) return false
      return true
    })
  }, [applications, filterStatus, filterVacancy])

  /* ── abrir modal perfil y cargar datos completos del colaborador ── */
  const openProfile = async (a: Application) => {
    setProfileModal(a)
    setColabProfile(null)
    setLoadingProfile(true)
    try {
      const { list } = await lumi.entities.collaborators.list({
        filter: { userId: a.collaboratorUserId },
        limit: 1,
      })
      if (list && list.length > 0) setColabProfile(list[0] as CollaboratorProfile)
    } catch (e) {
      console.error('Error al cargar perfil del colaborador:', e)
    } finally {
      setLoadingProfile(false)
    }
  }

  /* ── abrir modal de agendar entrevista ── */
  const openInterview = (a: Application) => {
    setInterviewModal(a)
    setIDate(a.interviewDate ? a.interviewDate.substring(0, 16) : '')
    setIAddress(a.interviewAddress || '')
    setIPhone(a.interviewPhone || '')
    setINotes(a.interviewNotes || '')
  }

  /* ── guardar propuesta de entrevista ── */
  const saveInterview = async () => {
    if (!interviewModal) return
    if (!iDate) { window.alert('Por favor selecciona una fecha y hora para la entrevista.'); return }
    setWorking(true)
    try {
      await updateInterview(interviewModal._id, {
        interviewDate: new Date(iDate).toISOString(),
        interviewAddress: iAddress,
        interviewPhone: iPhone,
        interviewNotes: iNotes,
        interviewStatus: 'pendiente_confirmacion',
        status: 'entrevista',
      })
      reload()
      setInterviewModal(null)
      // Actualizar el modal de perfil si está abierto con el mismo registro
      if (profileModal && profileModal._id === interviewModal._id) {
        setProfileModal((prev) => prev ? {
          ...prev,
          status: 'entrevista',
          interviewDate: new Date(iDate).toISOString(),
          interviewAddress: iAddress,
          interviewPhone: iPhone,
          interviewNotes: iNotes,
          interviewStatus: 'pendiente_confirmacion',
        } : null)
      }
    } catch (e) {
      console.error('Error al guardar entrevista:', e)
      window.alert('No se pudo guardar la propuesta de entrevista.')
    } finally {
      setWorking(false)
    }
  }

  /* ── marcar asistencia ── */
  const markAttendance = async (a: Application, attended: boolean) => {
    setWorking(true)
    try {
      await updateInterview(a._id, {
        interviewStatus: attended ? 'asistio' : 'no_asistio',
      })
      reload()
      setProfileModal((prev) =>
        prev && prev._id === a._id
          ? { ...prev, interviewStatus: attended ? 'asistio' : 'no_asistio' }
          : prev,
      )
    } catch (e) {
      console.error('Error al registrar asistencia:', e)
      window.alert('No se pudo registrar la asistencia.')
    } finally {
      setWorking(false)
    }
  }

  /* ── cambiar estado general ── */
  const handleStatus = async (a: Application, status: Application['status']) => {
    setWorking(true)
    try {
      await updateStatus(a._id, status)

      // Notificar al colaborador cuando es aceptado o rechazado
      if ((status === 'aceptado' || status === 'rechazado') && a.collaboratorUserId) {
        const aceptado = status === 'aceptado'
        await notifyUser({
          targetUserId: a.collaboratorUserId,
          title: aceptado ? 'Postulación aceptada' : 'Postulación no seleccionada',
          body: aceptado
            ? `¡Felicidades! Tu postulación a "${a.vacancyTitle || 'la vacante'}" fue aceptada.`
            : `Tu postulación a "${a.vacancyTitle || 'la vacante'}" no fue seleccionada en esta ocasión.`,
          type: 'vacante',
          audience: 'colaboradores',
          linkUrl: '/colaborador/postulaciones',
          creator: user?.userId,
        })
      }

      // Refrescar el badge de postulantes pendientes en el sidebar
      window.dispatchEvent(new Event('beeznz:applications-changed'))

      reload()
      setProfileModal((prev) => prev && prev._id === a._id ? { ...prev, status } : prev)
    } catch (e) {
      console.error('Error al actualizar estado:', e)
      window.alert('No se pudo actualizar el estado de la postulación.')
    } finally {
      setWorking(false)
    }
  }

  const cvUrl = profileModal?.cvUrl || colabProfile?.cvUrl

  return (
    <EmpresaLayout title="Postulantes" subtitle="Revisa candidatos, descarga CV, agenda entrevistas y toma decisiones">

      {/* Filtros */}
      <div className="mb-5 flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-4">
        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600">
          <Filter size={15} /> Filtrar
        </span>
        <select
          value={filterVacancy}
          onChange={(e) => setFilterVacancy(e.target.value)}
          className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 focus:border-amber-400 focus:outline-none"
        >
          <option value="todas">Todas las vacantes</option>
          {vacancyNames.map(([id, name]) => (
            <option key={id} value={id}>{name}</option>
          ))}
        </select>
        <div className="flex flex-wrap gap-1.5">
          <button
            onClick={() => setFilterStatus('todos')}
            className={`rounded-full px-3 py-1 text-xs font-semibold transition ${filterStatus === 'todos' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          >
            Todos
          </button>
          {statusOptions.map((s) => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition ${filterStatus === s ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            >
              {APPLICATION_STATUS_LABELS[s]}
            </button>
          ))}
        </div>
      </div>

      {/* Lista */}
      {loading ? (
        <div className="flex justify-center py-20 text-sm text-slate-400">Cargando postulantes...</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white py-16 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
            <Users size={22} />
          </div>
          <p className="mt-4 font-semibold text-slate-900">Sin postulantes</p>
          <p className="mt-1 text-sm text-slate-500">Cuando alguien se postule a tus vacantes aparecerá aquí.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {filtered.map((a) => (
            <div key={a._id} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-900 text-sm font-bold text-amber-400">
                    {(a.collaboratorName || a.collaboratorEmail || 'C').charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900">{a.collaboratorName || 'Candidato'}</p>
                    <p className="text-xs text-slate-500 inline-flex items-center gap-1"><Briefcase size={12} /> {a.vacancyTitle || 'Vacante'}</p>
                    <p className="mt-0.5 text-xs text-slate-400">Postuló el {fmtDate(a.createdAt)}</p>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${APPLICATION_STATUS_STYLES[a.status]}`}>
                    {APPLICATION_STATUS_LABELS[a.status]}
                  </span>
                  {a.interviewStatus && (
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${INTERVIEW_STATUS_STYLES[a.interviewStatus]}`}>
                      {INTERVIEW_STATUS_LABELS[a.interviewStatus]}
                    </span>
                  )}
                </div>
              </div>

              {/* Info entrevista resumida */}
              {a.interviewDate && (
                <div className="mt-3 flex items-center gap-2 rounded-lg bg-violet-50 px-3 py-2 text-xs text-violet-700">
                  <AlarmClock size={13} />
                  <span>Entrevista: <strong>{fmtDateTime(a.interviewDate)}</strong></span>
                  {a.interviewAddress && <span>— {a.interviewAddress}</span>}
                </div>
              )}

              <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
                <button
                  onClick={() => openProfile(a)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
                >
                  <Eye size={13} /> Ver perfil
                </button>
                {(a.cvUrl) ? (
                  <a href={a.cvUrl} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50">
                    <Download size={13} /> Descargar CV
                  </a>
                ) : null}
                <button
                  onClick={() => openInterview(a)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-violet-200 px-3 py-1.5 text-xs font-medium text-violet-700 hover:bg-violet-50"
                >
                  <Calendar size={13} /> {a.interviewDate ? 'Editar entrevista' : 'Agendar entrevista'}
                </button>
                {/* Marcar asistencia si la entrevista está confirmada o ya pasó */}
                {a.status === 'entrevista' && a.interviewStatus === 'confirmada' && (
                  <>
                    <button onClick={() => markAttendance(a, true)} disabled={working}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-50">
                      <CheckCircle2 size={13} /> Asistió
                    </button>
                    <button onClick={() => markAttendance(a, false)} disabled={working}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50">
                      <XCircle size={13} /> No asistió
                    </button>
                  </>
                )}
                <button onClick={() => handleStatus(a, 'aceptado')} disabled={working}
                  className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-50">
                  <Check size={13} /> Aceptar
                </button>
                <button onClick={() => handleStatus(a, 'rechazado')} disabled={working}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50">
                  <X size={13} /> Rechazar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ══════════════════════════════════════
          MODAL — Perfil completo del postulante
          ══════════════════════════════════════ */}
      {profileModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setProfileModal(null)}
        >
          <div
            className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 z-10 flex items-center justify-between rounded-t-2xl bg-slate-900 px-6 py-4">
              <div className="flex items-center gap-3">
                {colabProfile?.photoUrl ? (
                  <img src={colabProfile.photoUrl} alt="foto" className="h-12 w-12 rounded-full object-cover ring-2 ring-amber-400" />
                ) : (
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-400 text-xl font-bold text-slate-900">
                    {(profileModal.collaboratorName || profileModal.collaboratorEmail || 'C').charAt(0).toUpperCase()}
                  </div>
                )}
                <div>
                  <h3 className="text-lg font-bold text-white">
                    {colabProfile?.fullName || profileModal.collaboratorName || 'Candidato'}
                  </h3>
                  <span className={`mt-0.5 inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${APPLICATION_STATUS_STYLES[profileModal.status]}`}>
                    {APPLICATION_STATUS_LABELS[profileModal.status]}
                  </span>
                </div>
              </div>
              <button onClick={() => setProfileModal(null)} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-800">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {loadingProfile ? (
                <p className="text-center text-sm text-slate-400 py-8">Cargando información del candidato...</p>
              ) : (
                <>
                  {/* Vacante postulada */}
                  <div className="rounded-lg bg-amber-50 border border-amber-100 px-4 py-3 flex items-center gap-2 text-sm text-amber-800">
                    <Briefcase size={15} className="shrink-0" />
                    <span>Postulado a: <strong>{profileModal.vacancyTitle || '—'}</strong></span>
                    <span className="ml-auto text-xs text-amber-600">Fecha: {fmtDate(profileModal.createdAt)}</span>
                  </div>

                  {/* Datos de contacto */}
                  <section>
                    <h4 className="mb-3 text-xs font-bold uppercase tracking-wide text-slate-400">Datos de contacto</h4>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 text-sm">
                      <InfoRow icon={<Mail size={14} />} label="Correo" value={colabProfile?.email || profileModal.collaboratorEmail} />
                      <InfoRow icon={<Phone size={14} />} label="Teléfono" value={colabProfile?.phone} />
                      <InfoRow icon={<MapPin size={14} />} label="Dirección" value={colabProfile?.address} />
                      <InfoRow icon={<MapPin size={14} />} label="Ciudad / Estado" value={[colabProfile?.city, colabProfile?.state].filter(Boolean).join(', ')} />
                    </div>
                  </section>

                  {/* Formación y experiencia */}
                  <section>
                    <h4 className="mb-3 text-xs font-bold uppercase tracking-wide text-slate-400">Formación y experiencia</h4>
                    <div className="space-y-2 text-sm">
                      <InfoRow icon={<Info size={14} />} label="Escolaridad" value={colabProfile?.education} />
                      {colabProfile?.experience && (
                        <div className="rounded-lg bg-slate-50 px-4 py-3 text-slate-700">
                          <p className="mb-1 text-xs font-semibold text-slate-400">Experiencia laboral</p>
                          <p className="whitespace-pre-wrap">{colabProfile.experience}</p>
                        </div>
                      )}
                    </div>
                  </section>

                  {/* Habilidades / idiomas / certificaciones */}
                  {((colabProfile?.skills?.length ?? 0) > 0 || (colabProfile?.languages?.length ?? 0) > 0 || (colabProfile?.certifications?.length ?? 0) > 0) && (
                    <section>
                      <h4 className="mb-3 text-xs font-bold uppercase tracking-wide text-slate-400">Competencias</h4>
                      <div className="space-y-3">
                        {(colabProfile?.skills?.length ?? 0) > 0 && (
                          <div>
                            <p className="mb-1.5 text-xs font-semibold text-slate-500">Habilidades</p>
                            <div className="flex flex-wrap gap-1.5">
                              {colabProfile!.skills!.map((s) => (
                                <span key={s} className="rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700">{s}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {(colabProfile?.languages?.length ?? 0) > 0 && (
                          <div>
                            <p className="mb-1.5 text-xs font-semibold text-slate-500">Idiomas</p>
                            <div className="flex flex-wrap gap-1.5">
                              {colabProfile!.languages!.map((l) => (
                                <span key={l} className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">{l}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {(colabProfile?.certifications?.length ?? 0) > 0 && (
                          <div>
                            <p className="mb-1.5 text-xs font-semibold text-slate-500">Certificaciones</p>
                            <div className="flex flex-wrap gap-1.5">
                              {colabProfile!.certifications!.map((c) => (
                                <span key={c} className="rounded-full bg-violet-50 px-2.5 py-0.5 text-xs font-medium text-violet-700">{c}</span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </section>
                  )}

                  {/* Pretención salarial mensual */}
                  {colabProfile?.salaryExpectation ? (
                    <section>
                      <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">Pretención salarial mensual</h4>
                      <p className="text-sm font-semibold text-slate-800">
                        ${colabProfile.salaryExpectation.toLocaleString('es-MX')} MXN
                      </p>
                    </section>
                  ) : null}

                  {/* Mensaje del candidato */}
                  {profileModal.coverNote && (
                    <section>
                      <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">Mensaje del candidato</h4>
                      <div className="rounded-lg bg-slate-50 px-4 py-3 text-sm text-slate-700 whitespace-pre-wrap">
                        {profileModal.coverNote}
                      </div>
                    </section>
                  )}

                  {/* Estado de entrevista */}
                  {profileModal.interviewDate && (
                    <section>
                      <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">Entrevista agendada</h4>
                      <div className="rounded-lg border border-violet-100 bg-violet-50 px-4 py-3 space-y-1.5 text-sm text-violet-800">
                        <p className="flex items-center gap-2"><Clock size={14} /> <strong>{fmtDateTime(profileModal.interviewDate)}</strong></p>
                        {profileModal.interviewAddress && <p className="flex items-center gap-2"><MapPin size={14} /> {profileModal.interviewAddress}</p>}
                        {profileModal.interviewPhone && <p className="flex items-center gap-2"><Phone size={14} /> {profileModal.interviewPhone}</p>}
                        {profileModal.interviewNotes && <p className="flex items-start gap-2"><Info size={14} className="mt-0.5 shrink-0" /> {profileModal.interviewNotes}</p>}
                        {profileModal.interviewStatus && (
                          <span className={`mt-1 inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${INTERVIEW_STATUS_STYLES[profileModal.interviewStatus]}`}>
                            {INTERVIEW_STATUS_LABELS[profileModal.interviewStatus]}
                          </span>
                        )}
                      </div>
                    </section>
                  )}
                </>
              )}

              {/* Acciones */}
              <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
                {cvUrl ? (
                  <a
                    href={cvUrl} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-amber-400 hover:bg-slate-800"
                  >
                    <FileText size={15} /> Descargar CV
                  </a>
                ) : (
                  <span className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-400">
                    <FileText size={15} /> Sin CV adjunto
                  </span>
                )}
                <button
                  onClick={() => { setProfileModal(null); openInterview(profileModal!) }}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-violet-200 px-4 py-2 text-sm font-medium text-violet-700 hover:bg-violet-50"
                >
                  <Calendar size={15} /> {profileModal.interviewDate ? 'Editar entrevista' : 'Agendar entrevista'}
                </button>
                {profileModal.status === 'entrevista' && profileModal.interviewStatus === 'confirmada' && (
                  <>
                    <button onClick={() => markAttendance(profileModal, true)} disabled={working}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-50">
                      <CheckCircle2 size={15} /> Asistió
                    </button>
                    <button onClick={() => markAttendance(profileModal, false)} disabled={working}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50">
                      <XCircle size={15} /> No asistió
                    </button>
                  </>
                )}
                <button onClick={() => handleStatus(profileModal, 'aceptado')} disabled={working}
                  className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-50">
                  <Check size={15} /> Aceptar
                </button>
                <button onClick={() => handleStatus(profileModal, 'rechazado')} disabled={working}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50">
                  <X size={15} /> Rechazar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════
          MODAL — Agendar / Editar entrevista
          ══════════════════════════════════════ */}
      {interviewModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setInterviewModal(null)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between rounded-t-2xl bg-violet-700 px-6 py-4">
              <div>
                <h3 className="text-lg font-bold text-white">Proponer entrevista</h3>
                <p className="text-xs text-violet-200 mt-0.5">
                  {interviewModal.collaboratorName || 'Candidato'} — {interviewModal.vacancyTitle || 'Vacante'}
                </p>
              </div>
              <button onClick={() => setInterviewModal(null)} className="rounded-lg p-1.5 text-violet-200 hover:bg-violet-600">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-xs text-slate-500 bg-violet-50 rounded-lg px-3 py-2 border border-violet-100">
                La fecha y hora propuesta será enviada al candidato para su confirmación. Una vez que el candidato confirme o rechace, podrás ver el estado actualizado.
              </p>

              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-600">
                  Fecha y hora de la entrevista *
                </label>
                <input
                  type="datetime-local"
                  value={iDate}
                  onChange={(e) => setIDate(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-violet-400 focus:outline-none"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-600">
                  Dirección o lugar de la entrevista
                </label>
                <input
                  type="text"
                  placeholder="Ej. Av. Insurgentes 100, Piso 3, Col. Centro, CDMX"
                  value={iAddress}
                  onChange={(e) => setIAddress(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 placeholder-slate-400 focus:border-violet-400 focus:outline-none"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-600">
                  Teléfono de contacto
                </label>
                <input
                  type="tel"
                  placeholder="Ej. 55 1234 5678"
                  value={iPhone}
                  onChange={(e) => setIPhone(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 placeholder-slate-400 focus:border-violet-400 focus:outline-none"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-600">
                  Indicaciones adicionales
                </label>
                <textarea
                  rows={3}
                  placeholder="Ej. Preguntar por Recursos Humanos, traer identificación oficial, código de acceso: 1234..."
                  value={iNotes}
                  onChange={(e) => setINotes(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 placeholder-slate-400 focus:border-violet-400 focus:outline-none resize-none"
                />
              </div>

              <div className="flex gap-2 pt-2">
                <button onClick={() => setInterviewModal(null)}
                  className="flex-1 rounded-lg border border-slate-200 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">
                  Cancelar
                </button>
                <button onClick={saveInterview} disabled={working || !iDate}
                  className="flex-1 rounded-lg bg-violet-700 py-2 text-sm font-semibold text-white hover:bg-violet-600 disabled:opacity-50">
                  {working ? 'Guardando...' : 'Enviar propuesta'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </EmpresaLayout>
  )
}

/* ── Componente auxiliar para filas de información ── */
function InfoRow({ icon, label, value }: { icon: React.ReactNode; label: string; value?: string | null }) {
  if (!value) return null
  return (
    <div className="flex items-start gap-2 text-slate-700">
      <span className="mt-0.5 shrink-0 text-slate-400">{icon}</span>
      <span className="text-slate-400 shrink-0">{label}:</span>
      <span className="font-medium">{value}</span>
    </div>
  )
}

export default EmpresaPostulantes
